import React, { useState, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getSessionMode, IDLE_TIMEOUT_MINUTES, SessionMode, setSessionMode } from '../utils/sessionPreferences';
import { getStoredSsoLoginHint, StoredSsoLoginHint, storeSsoLoginHint } from '../utils/ssoLoginHint';
import { getActiveKoppelIntent, getPostLoginPath } from '../utils/koppelIntent';

const LoginPage: React.FC = () => {
    const { t } = useTranslation();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [codeDigits, setCodeDigits] = useState(['', '', '', '', '']);
    const [codeRequested, setCodeRequested] = useState(false);
    const [loginMethod, setLoginMethod] = useState<'password' | 'both' | null>(null);
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [isSsoLoggingIn, setIsSsoLoggingIn] = useState(false);
    const [isDeterminingMethod, setIsDeterminingMethod] = useState(false);
    const [isRequestingCode, setIsRequestingCode] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
    const [isRequestingReset, setIsRequestingReset] = useState(false);
    const [isDomainOnline, setIsDomainOnline] = useState<boolean | null>(null);
    const [showCodeSentMessage, setShowCodeSentMessage] = useState(false);
    const [error, setError] = useState('');
    const [isCheckingDomain, setIsCheckingDomain] = useState(false);
    const [sessionMode, setSessionModeState] = useState<SessionMode>(() => getSessionMode());
    const [showAlternativeLogin, setShowAlternativeLogin] = useState(false);

    const {
        determineLoginMethod,
        verifyLoginCode,
        verifyPassword: verifyPasswordApi,
        requestLoginCode,
        getSsoLoginUrl,
        exchangeSsoCode,
        checkDomainStatus,
        getDomain,
        sessionExpired,
        clearSessionExpired
    } = useAuth();

    const [lastSsoLogin, setLastSsoLogin] = useState<StoredSsoLoginHint | null>(() => getStoredSsoLoginHint(getDomain()));
    const showStoredSsoLogin = lastSsoLogin !== null && !showAlternativeLogin;
    const navigate = useNavigate();
    // Komt de gebruiker van de koppelpagina, dan zeggen waarom er ingelogd moet worden
    const [koppelIntent] = useState(() => getActiveKoppelIntent());

    useEffect(() => {
        const checkDomain = async () => {
            setIsCheckingDomain(true);
            try {
                const isOnline = await checkDomainStatus();
                setIsDomainOnline(isOnline);
            } catch (error) {
                setIsDomainOnline(false);
            } finally {
                setIsCheckingDomain(false);
            }
        };

        void checkDomain();
        setLastSsoLogin(getStoredSsoLoginHint(getDomain()));
    }, [checkDomainStatus, getDomain]);

    const sessionModeDescriptions: Record<SessionMode, string> = {
        idle_timeout: t('login.sessionMode.descriptions.idle_timeout', { minutes: IDLE_TIMEOUT_MINUTES }),
        stay_logged_in: t('login.sessionMode.descriptions.stay_logged_in'),
        session_only: t('login.sessionMode.descriptions.session_only')
    };

    const handleSessionModeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const nextMode = event.target.value as SessionMode;
        setSessionModeState(nextMode);
        setSessionMode(nextMode);
    };

    const persistSessionMode = () => {
        setSessionMode(sessionMode);
    };

    const isLoginButtonActive = (): boolean => {
        return isDomainOnline === true && email.trim() !== '';
    };

    const handleDetermineAndInitiateLogin = async () => {
        if (!isLoginButtonActive()) return;

        persistSessionMode();
        setIsDeterminingMethod(true);
        setError('');
        clearSessionExpired(); // Verberg sessie verlopen melding

        try {
            const result = await determineLoginMethod(email);

            if (result.method === 'sso') {
                if (result.orgIdentifier) {
                    await handleSsoLogin(result.orgIdentifier, email.trim());
                } else {
                    setError(t('login.errors.ssoNoOrg'));
                }
            } else if (result.method === 'code') {
                setCodeRequested(true);
                setShowCodeSentMessage(true);
                setTimeout(() => {
                    setShowCodeSentMessage(false);
                }, 5000);
            } else if (result.method === 'password') {
                setLoginMethod('password');
            } else if (result.method === 'both') {
                setLoginMethod('both');
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : t('common.errors.network'));
        } finally {
            setIsDeterminingMethod(false);
        }
    };

    const handleSsoLogin = async (orgIdentifier: string, loginEmail?: string) => {
        setIsSsoLoggingIn(true);
        setError('');

        const ssoUrl = getSsoLoginUrl(orgIdentifier);

        // De backend die de SSO success HTML serveert is dezelfde host als de SSO URL.
        // We accepteren postMessages alleen vanaf die origin.
        let expectedOrigin: string;
        try {
            expectedOrigin = new URL(ssoUrl).origin;
        } catch (e) {
            setError(t('login.errors.invalidSsoUrl'));
            setIsSsoLoggingIn(false);
            return;
        }

        // Open popup
        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const popup = window.open(
            ssoUrl,
            'SSO Login',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
        );

        if (!popup) {
            setError(t('login.errors.popupBlocked'));
            setIsSsoLoggingIn(false);
            return;
        }

        // De backend stuurt het success-bericht bewust meerdere keren kort na elkaar
        // voor browser-betrouwbaarheid; zonder dedupe zouden we dezelfde one-time code
        // meermaals proberen in te wisselen.
        const handledSsoCodes = new Set<string>();
        let popupCheckInterval: number | undefined;

        const messageHandler = async (event: MessageEvent) => {
            if (event.origin !== expectedOrigin) return;

            const data = event.data;
            if (!data || typeof data !== 'object') return;

            if (data.type === 'sso_login_error') {
                window.removeEventListener('message', messageHandler);
                if (popupCheckInterval !== undefined) {
                    clearInterval(popupCheckInterval);
                    popupCheckInterval = undefined;
                }
                if (!popup.closed) popup.close();
                const message = typeof data.message === 'string' && data.message
                    ? data.message
                    : t('login.errors.ssoFailed');
                setError(message);
                setIsSsoLoggingIn(false);
                return;
            }

            if (data.type !== 'sso_login_success') return;
            if (!data.code || handledSsoCodes.has(data.code)) return;

            handledSsoCodes.add(data.code);

            window.removeEventListener('message', messageHandler);
            if (popupCheckInterval !== undefined) {
                clearInterval(popupCheckInterval);
                popupCheckInterval = undefined;
            }

            if (!popup.closed) {
                popup.close();
            }

            try {
                const success = await exchangeSsoCode(data.code);
                if (success) {
                    const storedHint: StoredSsoLoginHint = {
                        domain: getDomain(),
                        orgIdentifier,
                        email: loginEmail,
                        updatedAt: new Date().toISOString()
                    };
                    storeSsoLoginHint(storedHint);
                    setLastSsoLogin(storedHint);
                    navigate(getPostLoginPath());
                } else {
                    setError(t('login.errors.ssoExchangeFailed'));
                }
            } catch (error) {
                setError(t('login.errors.ssoExchangeError'));
            } finally {
                setIsSsoLoggingIn(false);
            }
        };

        window.addEventListener('message', messageHandler);

        // Reset loading state als de gebruiker de popup sluit zonder login te voltooien.
        popupCheckInterval = window.setInterval(() => {
            if (popup.closed) {
                if (popupCheckInterval !== undefined) {
                    clearInterval(popupCheckInterval);
                    popupCheckInterval = undefined;
                }
                window.removeEventListener('message', messageHandler);
                setTimeout(() => {
                    setIsSsoLoggingIn((prev) => (prev ? false : prev));
                }, 1000);
            }
        }, 1000);
    };

    const handleStoredSsoLogin = async () => {
        if (!lastSsoLogin || isDomainOnline !== true) {
            return;
        }

        persistSessionMode();
        clearSessionExpired();
        await handleSsoLogin(lastSsoLogin.orgIdentifier, lastSsoLogin.email);
    };

    const handleVerifyCode = async () => {
        if (!isLoginButtonActive() || getCompleteCode().length !== 5) return;

        persistSessionMode();
        setIsLoggingIn(true);
        setError('');
        clearSessionExpired(); // Verberg sessie verlopen melding

        try {
            const success = await verifyLoginCode(email, getCompleteCode());
            if (success) {
                navigate(getPostLoginPath());
            } else {
                setError(t('login.errors.invalidCode'));
                clearCodeFields();
            }
        } catch (error) {
            setError(t('common.errors.network'));
            clearCodeFields();
        } finally {
            setIsLoggingIn(false);
        }
    };

    const clearCodeFields = () => {
        setCodeDigits(['', '', '', '', '']);
        // Focus op het eerste veld
        const firstInput = document.getElementById('code-0') as HTMLInputElement;
        if (firstInput) firstInput.focus();
    };

    const getCompleteCode = (): string => {
        return codeDigits.join('');
    };

    const handleCodeInputChange = (index: number, value: string) => {
        if (value.length > 1) {
            // Plak-logica voor meerdere cijfers
            const pastedText = value.replace(/[^0-9]/g, '');
            const newCodeDigits = [...codeDigits];

            // Zet eerste cijfer in huidige veld
            newCodeDigits[index] = pastedText[0] || '';

            // Verdeel overige cijfers over volgende velden
            for (let i = 1; i < pastedText.length && index + i < 5; i++) {
                newCodeDigits[index + i] = pastedText[i];
            }

            setCodeDigits(newCodeDigits);

            // Focus naar laatste veld als code compleet is
            if (pastedText.length >= 5) {
                const lastInput = document.getElementById('code-4') as HTMLInputElement;
                if (lastInput) lastInput.focus();
                // Auto-verify als code compleet is
                setTimeout(() => {
                    if (newCodeDigits.join('').length === 5) {
                        handleVerifyCode();
                    }
                }, 100);
            }
        } else if (value.match(/^[0-9]$/)) {
            // Enkele cijfer invoer
            const newCodeDigits = [...codeDigits];
            newCodeDigits[index] = value;
            setCodeDigits(newCodeDigits);

            if (index < 4) {
                const nextInput = document.getElementById(`code-${index + 1}`) as HTMLInputElement;
                if (nextInput) nextInput.focus();
            }

            // Auto-verify als code compleet is
            if (newCodeDigits.join('').length === 5) {
                setTimeout(() => handleVerifyCode(), 100);
            }
        } else if (value === '') {
            // Backspace handling
            const newCodeDigits = [...codeDigits];
            newCodeDigits[index] = '';
            setCodeDigits(newCodeDigits);
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && codeDigits[index] === '' && index > 0) {
            const prevInput = document.getElementById(`code-${index - 1}`) as HTMLInputElement;
            if (prevInput) prevInput.focus();
        }
    };

    const handleBackToEmail = () => {
        setCodeRequested(false);
        setLoginMethod(null);
        setShowCodeSentMessage(false);
        setCodeDigits(['', '', '', '', '']);
        setPassword('');
        setShowForgotPassword(false);
        setForgotPasswordMessage('');
        setError('');
    };

    const handleVerifyPassword = async () => {
        if (!isLoginButtonActive() || password.trim() === '') return;

        persistSessionMode();
        setIsLoggingIn(true);
        setError('');
        clearSessionExpired();

        try {
            const success = await verifyPasswordApi(email, password);
            if (success) {
                navigate(getPostLoginPath());
            } else {
                setError(t('login.errors.loginFailed'));
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : t('common.errors.network'));
        } finally {
            setIsLoggingIn(false);
        }
    };

    const handleSwitchToCode = async () => {
        setIsRequestingCode(true);
        setError('');

        try {
            const success = await requestLoginCode(email);
            if (success) {
                setLoginMethod(null);
                setCodeRequested(true);
                setShowCodeSentMessage(true);
                setTimeout(() => {
                    setShowCodeSentMessage(false);
                }, 5000);
            } else {
                setError(t('login.errors.codeRequestFailed'));
            }
        } catch (error) {
            setError(t('login.errors.codeRequestError'));
        } finally {
            setIsRequestingCode(false);
        }
    };

    const handleRequestPasswordReset = async () => {
        setIsRequestingReset(true);
        setError('');

        try {
            const authService = (await import('../services/authService')).default.getInstance();
            const message = await authService.requestPasswordReset(email);
            setForgotPasswordMessage(message);
        } catch (error) {
            setForgotPasswordMessage(t('login.password.resetEmailSent'));
        } finally {
            setIsRequestingReset(false);
        }
    };

    const handleOpenSettings = () => {
        navigate('/settings');
    };

    return (
        <div className="min-h-screen bg-background flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none">
                <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-primary-200/20 blur-3xl"></div>
                <div className="absolute top-[40%] -left-[10%] w-[40%] h-[40%] rounded-full bg-primary-300/20 blur-3xl"></div>
            </div>

            <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                {/* Header Actions */}
                <div className="absolute top-0 right-0 -mt-16 mr-4 sm:mr-0 flex items-center gap-2">
                    <LanguageSwitcher tone="light" />
                    <button
                        onClick={handleOpenSettings}
                        className="p-2 rounded-full text-gray-500 hover:bg-white/50 hover:text-primary-600 transition-colors"
                        title={t('common.settings')}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    </button>
                </div>

                {isDomainOnline === false && (
                    <div role="alert" className="mt-8 mb-4 flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <div className="h-2 w-2 flex-shrink-0 rounded-full bg-red-500" aria-hidden="true"></div>
                        <span>{t('login.serverOffline')}</span>
                        {isCheckingDomain && (
                            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        )}
                    </div>
                )}

                {/* Session Expired Notice */}
                {sessionExpired && (
                    <div className="bg-yellow-50 border-b border-yellow-200 p-4">
                        <div className="max-w-md mx-auto flex items-center">
                            <svg className="h-5 w-5 text-yellow-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            <div className="flex-1">
                                <p className="text-sm text-yellow-800 font-medium">
                                    {t('login.sessionExpired.title')}
                                </p>
                                <p className="text-xs text-yellow-700 mt-1">
                                    {t('login.sessionExpired.body')}
                                </p>
                            </div>
                            <button
                                onClick={clearSessionExpired}
                                className="ml-4 text-yellow-600 hover:text-yellow-800"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {koppelIntent && (
                    <div role="status" className="mt-8 flex items-start gap-2 rounded-xl border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800">
                        <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5M10.172 13.828a4 4 0 010-5.656l3-3a4 4 0 015.656 5.656l-1.5 1.5" />
                        </svg>
                        <span>
                            {koppelIntent.partnerName
                                ? t('koppelPage.loginPrompt', { partner: koppelIntent.partnerName })
                                : t('koppelPage.loginPromptGeneric')}
                        </span>
                    </div>
                )}

                {/* Content */}
                <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
                    <div className="bg-white py-8 px-4 shadow-hard sm:rounded-2xl sm:px-10 border border-white/50 backdrop-blur-sm">
                        {codeRequested ? (
                            // Code Verification View
                            <>
                                <div className="text-center">
                                    <button
                                        onClick={handleBackToEmail}
                                        className="absolute top-4 left-4 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                                        title={t('common.back')}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h2 className="text-lg font-bold text-gray-900 mb-2">
                                        {t('login.code.title')}
                                    </h2>
                                    <p className="text-sm text-gray-600 mb-4">
                                        <Trans
                                            i18nKey="login.loggingInAs"
                                            values={{ email }}
                                            components={{ strong: <span className="font-medium text-gray-900" /> }}
                                        />
                                    </p>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
                                        <svg className="h-5 w-5 text-red-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-red-600">{error}</p>
                                    </div>
                                )}

                                {showCodeSentMessage && (
                                    <div className="bg-green-50 border border-green-200 rounded-md p-3 flex items-center">
                                        <svg className="h-5 w-5 text-green-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-green-600">{t('login.code.sent')}</p>
                                    </div>
                                )}

                                <div className="bg-white rounded-xl p-6 shadow-sm">
                                    <div className="flex justify-center space-x-3 mb-6">
                                        {codeDigits.map((digit, index) => (
                                            <input
                                                key={index}
                                                id={`code-${index}`}
                                                type="text"
                                                maxLength={5}
                                                value={digit}
                                                onChange={(e) => handleCodeInputChange(index, e.target.value)}
                                                onKeyDown={(e) => handleKeyDown(index, e)}
                                                className="w-12 h-12 text-center text-lg font-semibold border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                            />
                                        ))}
                                    </div>

                                    <button
                                        onClick={handleVerifyCode}
                                        disabled={isLoggingIn || getCompleteCode().length !== 5 || !isLoginButtonActive()}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        {isLoggingIn ? (
                                            <div className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('login.code.verifying')}
                                            </div>
                                        ) : (
                                            t('login.code.verify')
                                        )}
                                    </button>
                                </div>
                            </>
                        ) : loginMethod ? (
                            // Password View
                            <>
                                <div className="text-center">
                                    <button
                                        onClick={handleBackToEmail}
                                        className="absolute top-4 left-4 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                                        title={t('common.back')}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                        </svg>
                                    </button>
                                    <h2 className="text-lg font-bold text-gray-900 mb-2">
                                        {t('login.password.title')}
                                    </h2>
                                    <p className="text-sm text-gray-600 mb-4">
                                        <Trans
                                            i18nKey="login.loggingInAs"
                                            values={{ email }}
                                            components={{ strong: <span className="font-medium text-gray-900" /> }}
                                        />
                                    </p>
                                </div>

                                {error && (
                                    <div className="bg-red-50 border border-red-200 rounded-md p-3 flex items-center mb-4">
                                        <svg className="h-5 w-5 text-red-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <p className="text-sm text-red-600">{error}</p>
                                    </div>
                                )}

                                <div className="bg-white rounded-xl p-6 shadow-sm">
                                    <div className="mb-4">
                                        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                                            {t('login.password.label')}
                                        </label>
                                        <input
                                            id="password"
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPassword(); }}
                                            className="block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                            placeholder={t('login.password.placeholder')}
                                            autoFocus
                                        />
                                    </div>

                                    <button
                                        onClick={handleVerifyPassword}
                                        disabled={isLoggingIn || password.trim() === '' || !isLoginButtonActive()}
                                        className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                    >
                                        {isLoggingIn ? (
                                            <div className="flex items-center">
                                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                {t('login.password.submitting')}
                                            </div>
                                        ) : (
                                            t('login.password.submit')
                                        )}
                                    </button>

                                    {loginMethod === 'both' && (
                                        <div className="mt-4 text-center">
                                            <button
                                                onClick={handleSwitchToCode}
                                                disabled={isRequestingCode}
                                                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors disabled:opacity-50"
                                            >
                                                {isRequestingCode ? (
                                                    <span className="flex items-center justify-center">
                                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        {t('login.password.requestingCode')}
                                                    </span>
                                                ) : (
                                                    t('login.password.loginWithCode')
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    <div className="mt-4 text-center">
                                        {showForgotPassword ? (
                                            forgotPasswordMessage ? (
                                                <div className="bg-green-50 border border-green-200 rounded-md p-3">
                                                    <p className="text-sm text-green-700">{forgotPasswordMessage}</p>
                                                    <button
                                                        onClick={() => { setShowForgotPassword(false); setForgotPasswordMessage(''); }}
                                                        className="mt-2 text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                                    >
                                                        {t('login.password.backToLogin')}
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="space-y-3">
                                                    <p className="text-sm text-gray-600">
                                                        <Trans
                                                            i18nKey="login.password.resetLinkTo"
                                                            values={{ email }}
                                                            components={{ strong: <span className="font-medium text-gray-900" /> }}
                                                        />
                                                    </p>
                                                    <button
                                                        onClick={handleRequestPasswordReset}
                                                        disabled={isRequestingReset}
                                                        className="w-full flex justify-center py-2.5 px-4 border border-gray-300 rounded-xl shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all"
                                                    >
                                                        {isRequestingReset ? (
                                                            <span className="flex items-center">
                                                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                {t('common.sending')}
                                                            </span>
                                                        ) : (
                                                            t('login.password.sendResetLink')
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => setShowForgotPassword(false)}
                                                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                                    >
                                                        {t('common.cancel')}
                                                    </button>
                                                </div>
                                            )
                                        ) : (
                                            <button
                                                onClick={() => setShowForgotPassword(true)}
                                                className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                                            >
                                                {t('login.password.forgot')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        ) : (
                            // Stored SSO or Email Input View
                            <>
                                <div className="text-center">
                                    <img src="/icons/icon.png" alt={t('login.logoAlt')} className="mx-auto h-20 w-20 mb-6 rounded-2xl shadow-lg transform rotate-3" />
                                    <h2 className="text-3xl font-bold text-gray-900 mb-2 font-gambarino">{t('login.welcomeBack')}</h2>
                                    <p className="text-gray-600">{t(showStoredSsoLogin ? 'login.sso.subtitle' : 'login.enterEmail')}</p>
                                </div>

                                <div className="mt-8">
                                    <div className="space-y-6">
                                        {showStoredSsoLogin && lastSsoLogin && (
                                            <div className="rounded-2xl border border-primary-100 bg-primary-50/70 p-4">
                                                <p className="text-sm font-semibold text-primary-900">{t('login.sso.continueTitle')}</p>
                                                <p className="mt-1 break-words text-sm text-primary-800">
                                                    {lastSsoLogin.email
                                                        ? <Trans
                                                            i18nKey="login.sso.lastUsedFor"
                                                            values={{ email: lastSsoLogin.email }}
                                                            components={{ strong: <span className="font-medium" /> }}
                                                        />
                                                        : t('login.sso.openLast')}
                                                </p>
                                                <button
                                                    onClick={handleStoredSsoLogin}
                                                    disabled={isSsoLoggingIn || isDeterminingMethod || isDomainOnline !== true}
                                                    className="mt-4 w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                                >
                                                    {isSsoLoggingIn ? t('login.sso.loggingIn') : t('login.sso.continue')}
                                                </button>
                                            </div>
                                        )}

                                        {!showStoredSsoLogin && (
                                            <>
                                                <div>
                                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                                        {t('login.emailLabel')}
                                                    </label>
                                                    <input
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        required
                                                        autoFocus={showAlternativeLogin}
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                                        placeholder={t('login.emailPlaceholder')}
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="session-mode" className="block text-sm font-medium text-gray-700">
                                                        {t('login.sessionMode.label')}
                                                    </label>
                                                    <select
                                                        id="session-mode"
                                                        value={sessionMode}
                                                        onChange={handleSessionModeChange}
                                                        className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                                                    >
                                                        <option value="idle_timeout">{t('login.sessionMode.idleOption', { minutes: IDLE_TIMEOUT_MINUTES })}</option>
                                                        <option value="session_only">{t('login.sessionMode.sessionOnlyOption')}</option>
                                                        <option value="stay_logged_in">{t('login.sessionMode.stayLoggedInOption')}</option>

                                                    </select>
                                                    <p className="mt-1 text-xs text-gray-500">{sessionModeDescriptions[sessionMode]}</p>
                                                </div>
                                            </>
                                        )}

                                        {error && (
                                            <div className="bg-red-50 border border-red-200 rounded-md p-3">
                                                <p className="text-sm text-red-600">{error}</p>
                                            </div>
                                        )}

                                        {!showStoredSsoLogin && (
                                            <button
                                                onClick={handleDetermineAndInitiateLogin}
                                                disabled={isDeterminingMethod || isSsoLoggingIn || !isLoginButtonActive()}
                                                className="w-full flex justify-center py-3.5 px-4 border border-transparent rounded-xl shadow-md text-sm font-bold text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
                                            >
                                                {(isDeterminingMethod || isSsoLoggingIn) ? (
                                                    <div className="flex items-center">
                                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        {isSsoLoggingIn ? t('login.sso.loggingIn') : t('login.determiningMethod')}
                                                    </div>
                                                ) : (
                                                    t('login.loginButton')
                                                )}
                                            </button>
                                        )}

                                        {lastSsoLogin && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setShowAlternativeLogin(!showAlternativeLogin);
                                                    setError('');
                                                }}
                                                disabled={isSsoLoggingIn || isDeterminingMethod}
                                                className="w-full rounded-md text-sm font-medium text-gray-600 underline underline-offset-4 hover:text-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                            >
                                                {t(showStoredSsoLogin ? 'login.sso.useAnotherMethod' : 'login.sso.backToSso')}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage; 
